import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Logo } from './logo';

export type SidebarLinkRenderProps = {
  to: string;
  className: string;
  children: ReactNode;
};

export type SidebarItem = {
  /** Destination : interprétée par `renderLink` (routeur de l'app appelante). */
  to: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  /** Compteur affiché à droite (file d'attente, messages non lus…). */
  count?: number;
};

export type SidebarGroup = {
  /** Intitulé de section, optionnel. */
  label?: string;
  items: readonly SidebarItem[];
};

export type SidebarProps = {
  /** Groupes du haut. Le dernier groupe est ancré en bas de la colonne. */
  groups: readonly SidebarGroup[];
  footerGroups?: readonly SidebarGroup[];
  /**
   * Rend un élément de navigation. Permet à chaque app d'utiliser son `Link`
   * de routeur sans que le design system dépende du routing.
   */
  renderLink?: (props: SidebarLinkRenderProps) => ReactNode;
  /** Encart libre en bas de colonne (promo, aide, état de connexion…). */
  footer?: ReactNode;
  /** Étiquette à côté du logo (« Admin »…) : distingue les apps qui partagent la même identité. */
  logoBadge?: ReactNode;
  className?: string;
};

const itemClass = (active?: boolean) =>
  cn(
    'group flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
    active ? 'bg-brand-800 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white',
  );

const iconClass = (active?: boolean) =>
  cn(
    'flex shrink-0 [&>svg]:size-6 transition-colors',
    active ? 'text-white' : 'text-white/50 group-hover:text-white/80',
  );

/** Navigation latérale, partagée par l'app utilisateurs et l'app admin. */
export function Sidebar({
  groups,
  footerGroups = [],
  renderLink,
  footer,
  logoBadge,
  className,
}: SidebarProps) {
  const link =
    renderLink ??
    (({ to, className: c, children }) => (
      <a href={to} className={c}>
        {children}
      </a>
    ));

  const renderGroup = (group: SidebarGroup, index: number) => (
    <div key={group.label ?? index} className="flex flex-col gap-1">
      {group.label ? (
        <p className="px-3 pb-1 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-white/35">
          {group.label}
        </p>
      ) : null}
      {group.items.map((item) => (
        <div key={item.to}>
          {link({
            to: item.to,
            className: itemClass(item.active),
            children: (
              <>
                <span className={iconClass(item.active)}>{item.icon}</span>
                <span className="flex-1 truncate">{item.label}</span>
                {item.count ? (
                  <span className="rounded-full bg-accent-500/20 px-2 py-0.5 text-[0.6875rem] font-semibold text-accent-300">
                    {item.count}
                  </span>
                ) : null}
              </>
            ),
          })}
        </div>
      ))}
    </div>
  );

  return (
    <aside className={cn('flex h-full w-60 shrink-0 flex-col bg-brand-600', className)}>
      <div className="flex h-16 shrink-0 items-center gap-2 border-b border-white/10 px-4">
        <Logo size="sm" tone="light" />
        {logoBadge ? (
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-semibold text-white/80">
            {logoBadge}
          </span>
        ) : null}
      </div>

      <nav className="flex flex-1 flex-col justify-between gap-6 overflow-y-auto p-4">
        <div className="flex flex-col gap-6">{groups.map(renderGroup)}</div>
        {footerGroups.length > 0 || footer ? (
          <div className="flex flex-col gap-4">
            {footer}
            {footerGroups.length > 0 ? (
              <div className="flex flex-col gap-6">{footerGroups.map(renderGroup)}</div>
            ) : null}
          </div>
        ) : null}
      </nav>
    </aside>
  );
}
